import { Field, FieldProps } from 'formik';
import { Form, Input, Button, Alert } from 'antd';
import React, { useState } from 'react';
import { get as _get } from 'lodash';
import { EyeInvisibleOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

type ObfuscatedSSHKeyInputProps = {
  name: string;
  label?: React.ReactNode;
  className?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  testId?: string;
  handleOnBlur?: () => void;
  handleChange?: (e: React.ChangeEvent<any>) => void;
  rows?: number;
};

const ObfuscatedSSHKeyInput: React.FC<ObfuscatedSSHKeyInputProps> = props => {
  const {
    name,
    label,
    className,
    placeholder,
    required,
    disabled,
    testId,
    handleOnBlur,
    handleChange,
    rows = 6,
  } = props;

  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const obfuscateKey = (key: string) => {
    if (!key) return '';
    // Show all stars to completely obfuscate the key
    return '*'.repeat(Math.min(key.length, 50)); // Limit to 50 stars for very long keys
  };

  // Detect if the key is already obfuscated by the backend
  // Backend returns obfuscated keys with first/last 40 chars only
  const isBackendObfuscated = (key: string) => {
    if (!key) return false;
    // Check if key appears to be obfuscated (short length or contains markers)
    const lines = key.trim().split('\n');
    // A full SSH key typically has many lines of base64 data
    // An obfuscated key will have very few lines (header + truncated data + footer)
    return lines.length <= 5 && key.includes('-----BEGIN') && key.includes('-----END');
  };

  return (
    <Field name={name}>
      {({ field, form }: FieldProps) => {
        const { onChange, onBlur } = field;
        const { errors, touched, setFieldValue } = form;
        const showValidationInfo = !!(_get(errors, name) && _get(touched, name));

        const isObfuscatedByBackend = isBackendObfuscated(field.value);
        const shouldShowAsReadOnly = isObfuscatedByBackend && !isEditing;

        // Obfuscate the key if:
        // 1. It's a backend-obfuscated key and not in edit mode, OR
        // 2. There's a value and visibility is off
        const shouldObfuscate = shouldShowAsReadOnly || (!isVisible && field.value);
        const displayValue = shouldObfuscate ? obfuscateKey(field.value) : field.value;

        const handleEdit = () => {
          setIsEditing(true);
          setFieldValue(name, '');
        };

        return (
          <Form.Item
            label={label}
            className={className}
            hasFeedback
            validateStatus={showValidationInfo ? 'error' : ''}
            help={(showValidationInfo && _get(errors, name)?.toString()) || undefined}
            required={required}
          >
            {isObfuscatedByBackend && !isEditing && (
              <Alert
                message={t('channel:sshKeyConfigured')}
                description={t('channel:sshKeyConfiguredDescription')}
                type="info"
                showIcon
                className="mb-2"
              />
            )}
            <div className="relative">
              <Input.TextArea
                data-testid={testId}
                name={name}
                placeholder={
                  shouldShowAsReadOnly
                    ? '••••••••••••••••••••'
                    : placeholder || t('channel:sshKeyPlaceholder')
                }
                value={displayValue}
                onChange={e => {
                  if (isObfuscatedByBackend && !isEditing) {
                    return; // Prevent editing obfuscated keys
                  }
                  if (handleChange) {
                    handleChange(e);
                  } else {
                    onChange(e);
                  }
                }}
                onBlur={handleOnBlur || onBlur}
                disabled={disabled || shouldShowAsReadOnly}
                rows={rows}
                style={{ paddingRight: '30px', fontFamily: 'monospace', resize: 'none' }}
              />
              {field.value && !shouldShowAsReadOnly && (
                <Button
                  type="text"
                  size="small"
                  icon={isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={toggleVisibility}
                  style={{ position: 'absolute', right: '5px', top: '5px', zIndex: 1 }}
                  disabled={disabled}
                />
              )}
              {isObfuscatedByBackend && !isEditing && !disabled && (
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  className="mt-2"
                >
                  {t('channel:sshKeyReplace')}
                </Button>
              )}
            </div>
          </Form.Item>
        );
      }}
    </Field>
  );
};

export default ObfuscatedSSHKeyInput;
